import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useGetPreApprovalDropdownQuery } from '../../api/endpoints/preApproval';

/**
 * PreApproval dropdown component
 * @param {Object} props
 * @param {string} props.value - The selected value (preAppNo)
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.preAppNo - Optional filter parameter
 * @param {string} props.chassisNo - Optional chassis number filter parameter
 */
const PreApprovalDropdown = React.memo(({ value, onChange, className = '', disabled = false, placeholder = 'Ön onay seçiniz', preAppNo = '', chassisNo = '' }) => {
    const [selectedPreApproval, setSelectedPreApproval] = useState(null);

    // Fetch pre-approval data from API
    const { data: preApprovalResponse, isLoading } = useGetPreApprovalDropdownQuery({ preAppNo, chassisNo });

    // Transform pre-approval list for dropdown with memoization
    const preApprovalOptions = useMemo(() => {
        if (!preApprovalResponse?.data) {
            return [];
        }

        return preApprovalResponse.data.map((item) => ({
            id: item.id,
            preAppNo: item.preAppNo,
            displayValue: item.preAppNo
        }));
    }, [preApprovalResponse]);

    // Handle selection change
    const handleSelectionChange = (e) => {
        setSelectedPreApproval(e.value);
        onChange(e.value?.preAppNo || null);
    };

    // Set initial value if available
    useEffect(() => {
        if (value && preApprovalOptions.length > 0) {
            const found = preApprovalOptions.find((opt) => opt.preAppNo === value);
            if (found) {
                setSelectedPreApproval(found);
            }
        } else {
            setSelectedPreApproval(null);
        }
    }, [value, preApprovalOptions]);

    return (
        <Dropdown
            value={selectedPreApproval}
            options={preApprovalOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            showClear
            className={className}
            placeholder={placeholder}
            loading={isLoading}
            disabled={disabled}
            filter
            virtualScrollerOptions={{ itemSize: 38 }}
            itemTemplate={(option) => (
                <div>
                    <b>{option.id}</b> - <span>{option.preAppNo}</span>
                </div>
            )}
        />
    );
});

PreApprovalDropdown.displayName = 'PreApprovalDropdown';

export default PreApprovalDropdown;
